/**
 * <pin-image-magnifier> — a circular "loupe" that follows the cursor on desktop
 * and a touch point on phones/tablets, showing a magnified crop of the wrapped
 * image. Pure progressive enhancement: if this script fails to load, the image
 * underneath still displays and behaves normally (including the theme's own
 * click-to-zoom dialog).
 *
 * Usage: wraps a single <div class="product-media"> containing an
 * <img class="product-media__image">. Reads its magnification strength from
 * [data-zoom-factor] and its high-resolution background source from
 * [data-zoom-src] (falls back to the image's own src).
 */
class PinImageMagnifier extends HTMLElement {
  /** @type {HTMLImageElement|null} */
  #image = null;
  /** @type {HTMLDivElement|null} */
  #lens = null;
  #zoomFactor = 2.5;
  #zoomSrc = '';
  #lensSizeMouse = 170;
  #lensSizeTouch = 140;
  #touching = false;

  connectedCallback() {
    this.#image = this.querySelector('img');
    if (!this.#image) return;

    this.#zoomFactor = parseFloat(this.dataset.zoomFactor || '') || this.#zoomFactor;
    this.#zoomSrc = this.dataset.zoomSrc || this.#image.currentSrc || this.#image.src;

    this.#lens = document.createElement('div');
    this.#lens.className = 'pin-magnifier__lens';
    this.#lens.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this.#lens);

    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    this.#lens?.remove();
    this.#lens = null;
  }

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#show();
    this.#update(event.clientX, event.clientY);
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (event.pointerType === 'mouse') {
      this.#update(event.clientX, event.clientY);
      return;
    }
    if (!this.#touching) return;
    event.preventDefault();
    this.#update(event.clientX, event.clientY);
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hide();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#touching = true;
    event.preventDefault();
    this.#show();
    this.#update(event.clientX, event.clientY);
  };

  #onPointerUp = () => {
    if (!this.#touching) return;
    this.#touching = false;
    this.#hide();
  };

  #show() {
    this.#lens?.classList.add('is-visible');
  }

  #hide() {
    this.#lens?.classList.remove('is-visible');
  }

  /**
   * @param {number} clientX
   * @param {number} clientY
   */
  #update(clientX, clientY) {
    const image = this.#image;
    const lens = this.#lens;
    if (!image || !lens) return;

    const rect = image.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const lensSize = matchMedia('(pointer: coarse)').matches ? this.#lensSizeTouch : this.#lensSizeMouse;
    const relX = clamp((clientX - rect.left) / rect.width, 0, 1);
    const relY = clamp((clientY - rect.top) / rect.height, 0, 1);

    const bgWidth = rect.width * this.#zoomFactor;
    const bgHeight = rect.height * this.#zoomFactor;
    const bgX = -(relX * bgWidth - lensSize / 2);
    const bgY = -(relY * bgHeight - lensSize / 2);

    const halfLens = lensSize / 2;
    const centerX = clampRange(clientX, rect.left + halfLens, rect.right - halfLens);
    const centerY = clampRange(clientY, rect.top + halfLens, rect.bottom - halfLens);

    lens.style.width = `${lensSize}px`;
    lens.style.height = `${lensSize}px`;
    lens.style.left = `${centerX - halfLens}px`;
    lens.style.top = `${centerY - halfLens}px`;
    lens.style.backgroundImage = `url("${this.#zoomSrc}")`;
    lens.style.backgroundSize = `${bgWidth}px ${bgHeight}px`;
    lens.style.backgroundPosition = `${bgX}px ${bgY}px`;
  }
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Like clamp, but tolerates min > max (narrow container) by returning min.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clampRange(value, min, max) {
  if (min > max) return min;
  return clamp(value, min, max);
}

if (!customElements.get('pin-image-magnifier')) {
  customElements.define('pin-image-magnifier', PinImageMagnifier);
}
